import { useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import Swal from 'sweetalert2';

import { Box, TextField } from '@mui/material';
import { SCUIButton } from '../../../atomic/atoms';

import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import MoveUpIcon from '@mui/icons-material/MoveUp';

import { SendToAxaptaStatus } from '../enum/SummaryEnums';
import { SummaryCommandV2 } from '../../../services/command';
import { SV } from '../summaryV2.classes';

const getLevelFromDisplayOrder = (displayOrder: any): number => {
    if (displayOrder == null) return 0;
    const str = String(displayOrder);
    if (!str) return 0;
    return (str.match(/\./g) || []).length;
};

const ReceiptItem = ({
    item,
    onChange,
    isEditing,
    value,
    valuesRef,
}: {
    item: any;
    onChange: (id: number | null, value: number) => void;
    isEditing: boolean;
    value?: number;
    valuesRef: any;
}) => {
    const definition = item?.summaryItemDefinition ?? {};

    const level = getLevelFromDisplayOrder(definition?.displayOrder);
    const cappedLevel = Math.min(level, 4);
    const fontSize = `${Math.max(18 - cappedLevel, 12)}px`;
    const fontWeight = Math.max(700 - cappedLevel * 100, 400);
    const sideInsetPx = cappedLevel * 8;

    const isOverriddenByUser = item?.isOverridenByUser === true;
    const hasPendingUserValue = valuesRef?.current[item?.id] !== undefined;
    const shouldUseGreen = definition?.coloredRow === true || hasPendingUserValue;

    const baseBackground = level === 0 ? '#bbb' : 'transparent';
    const rowBackgroundColor = isOverriddenByUser ? '#fff3cd' : shouldUseGreen ? '#c8f7c5' : baseBackground;

    const rowHoverBackgroundColor = isOverriddenByUser ? '#ffe8a3' : shouldUseGreen ? '#a8e6a2' : '#a0a0a0';

    const isReadonly = definition?.readonly === true;
    const currencySymbol = definition?.currencySymbol;
    const titleText = definition?.description ?? '';

    return (
        <Box
            className={SV.receiptItem}
            data-item-id={item?.id}
            data-display-order={definition?.displayOrder}
            sx={{
                display: 'flex',
                alignItems: 'center',
                paddingY: 0.5,
                paddingLeft: `${sideInsetPx + 12}px`,
                paddingRight: `${sideInsetPx + 12}px`,
                backgroundColor: rowBackgroundColor,
                '&:hover': {
                    backgroundColor: rowHoverBackgroundColor,
                },
            }}
        >
            <Box className={SV.receiptItemTitleBox} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                <p className={SV.receiptItemTitle} style={{ fontWeight, fontSize, margin: 0 }}>
                    {titleText}
                </p>
            </Box>
            <Box className={SV.receiptItemAmountBox} sx={{ minWidth: 120, textAlign: 'right' }}>
                {isEditing && !isReadonly ? (
                    <TextField
                        className={SV.receiptItemAmountInput}
                        size="small"
                        defaultValue={item?.amount}
                        variant="outlined"
                        sx={{
                            width: 300,
                            '& input': {
                                fontSize,
                                paddingY: 0.5,
                            },
                        }}
                        onChange={(e) => {
                            if (e.target.value === '0') {
                                Swal.fire({
                                    icon: 'warning',
                                    title: t('general.warning'),
                                    text: t('summary.amountIsZero'),
                                    showConfirmButton: false,
                                    timer: 1500,
                                });
                                return;
                            }
                            const raw = e.target.value;
                            let normalized = raw;

                            if (raw.includes(',') && raw.includes('.')) {
                                if (raw.lastIndexOf(',') > raw.lastIndexOf('.')) {
                                    normalized = raw.replace(/\./g, '').replace(',', '.');
                                } else {
                                    normalized = raw.replace(/,/g, '');
                                }
                            } else if (raw.includes(',')) {
                                normalized = raw.replace(/\./g, '').replace(',', '.');
                            } else if (raw.includes('.')) {
                                normalized = raw.replace(/\./g, '');
                            } else {
                                normalized = raw;
                            }

                            const newValue = parseFloat(normalized) || 0;
                            onChange(item?.id, newValue);
                        }}
                    />
                ) : (
                    <p className={SV.receiptItemAmount} style={{ fontSize }}>
                        {(() => {
                            const displayValue = value ?? item?.amount ?? 0;
                            const formatted = Number(displayValue).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            });
                            return currencySymbol ? `${formatted} ${currencySymbol}` : formatted;
                        })()}
                    </p>
                )}
            </Box>
        </Box>
    );
};

const prepareSummaryItems = (items: any[]): any[] => {
    if (!items?.length) return [];

    return items
        .filter((item) => item?.summaryItemDefinition?.visible !== false)
        .slice()
        .sort((a, b) => {
            const aOrder = String(a?.summaryItemDefinition?.displayOrder ?? '');
            const bOrder = String(b?.summaryItemDefinition?.displayOrder ?? '');
            return aOrder.localeCompare(bOrder, undefined, { numeric: true });
        });
};

const collectChangedItems = (items: any[], amounts: any, summaryInfo: any) => {
    return (items || [])
        .filter((item) => item.id in amounts && amounts[item.id] !== '' && amounts[item.id] !== 0)
        .map((item) => ({
            summaryItemId: item.id,
            summaryId: summaryInfo.id,
            amount: amounts[item.id],
            isOverridenByUser: true,
            computedAmount: amounts[item.id],
            summaryItemDefinitionId: item.summaryItemDefinitionId,
        }));
};

export const ReceiptRenderer = ({ data, summaryInfo, setIsLoading, getSummaryList }: any) => {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const amountsRef = useRef<{ [id: number]: number }>({});

    const sortedItems = useMemo(() => prepareSummaryItems(data || []), [data]);

    const handleChange = (id: number | null, value: number) => {
        if (id != null) {
            amountsRef.current[id] = value;
        }
    };

    const handleSubmit = () => {
        const collectedData = collectChangedItems(sortedItems, amountsRef.current, summaryInfo);

        const params = {
            summary: {
                summaryId: summaryInfo.id,
                periodEndDate: summaryInfo.periodEndDate,
                currentVersionId: summaryInfo.currentVersionId,
                summaryItemList: collectedData,
            },
        };

        SummaryCommandV2.UpdateSummary(params)
            .then((res: any) => {
                if (res) {
                    setIsEditing(false);
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    amountsRef.current = {};
                    getSummaryList();
                } else {
                    Swal.fire(t('general.anErrorOccured', '', 'error'));
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
            });
    };

    const toggleEditing = () => {
        if (isEditing) {
            handleSubmit();
            return;
        }
        setIsEditing(true);
    };

    const handleAxapta = () => {
        setIsLoading(true);
        SummaryCommandV2.AxaptaTransferSummary({
            consolidationId: summaryInfo?.id,
        })
            .then((res: any) => {
                if (res) {
                    Swal.fire({
                        icon: 'success',
                        title: t('general.success'),
                        showConfirmButton: false,
                        timer: 1500,
                    });
                    getSummaryList?.();
                }
                setIsLoading(false);
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured', '', 'error'));
                setIsLoading(false);
            });
    };

    const canTransferToAxapta =
        summaryInfo.sendToAxaptaStatus !== SendToAxaptaStatus.TransferCompleted;

    return (
        <Box
            className={SV.receipt}
            sx={{ p: 2, width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column' }}
        >
            <Box className={SV.receiptActions} sx={{ display: 'flex', gap: 2, mb: 2, justifyContent: 'center' }}>
                <SCUIButton
                    className={SV.btnEditSave}
                    onClick={toggleEditing}
                    type="primary"
                    sizeType="fixed"
                    size="m"
                    text={isEditing ? t('general.save') : t('general.edit')}
                >
                    {isEditing ? (
                        <SaveIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                    ) : (
                        <EditIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                    )}
                </SCUIButton>
                {canTransferToAxapta && (
                    <SCUIButton
                        className={`${SV.btnReceiptStatus} ${SV.btnReceiptTransfer}`}
                        onClick={handleAxapta}
                        type="primary"
                        sizeType="fixed"
                        size="m"
                        text={t('summary.transferToAxapta')}
                    >
                        <MoveUpIcon sx={{ fill: '#fff', marginLeft: '12px' }} />
                    </SCUIButton>
                )}
            </Box>
            {sortedItems.map((item, index) => {
                const itemKey = item?.id ?? item?.summaryItemDefinitionId ?? index;
                const storedValue = amountsRef.current[item.id];
                return (
                    <ReceiptItem
                        key={itemKey}
                        item={item}
                        onChange={handleChange}
                        isEditing={isEditing}
                        valuesRef={amountsRef}
                        value={storedValue}
                    />
                );
            })}
        </Box>
    );
};
